import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const COLORS = {
  public:   '#10B981',
  private:  '#6B35B8',
  password: '#F5A623',
  infra:    '#b0b4c0',
};

function StatusDot({ status }) {
  const color = status === 'ok' ? '#10B981' : status === 'error' ? '#EF4444' : '#454860';
  return (
    <span style={{
      display: 'inline-block',
      width: 7, height: 7, borderRadius: '50%',
      background: color,
      boxShadow: status === 'ok' ? `0 0 6px ${color}90` : 'none',
      flexShrink: 0,
      transition: 'background 0.4s ease, box-shadow 0.4s ease',
    }} />
  );
}

function StatusPill({ status }) {
  const color = status === 'ok' ? '#10B981' : status === 'error' ? '#EF4444' : '#454860';
  const label = status === 'ok' ? 'Live' : status === 'error' ? 'Down' : '—';
  return (
    <span style={{
      fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
      fontSize: 9,
      letterSpacing: '0.1em',
      color,
      background: `${color}18`,
      border: `0.5px solid ${color}55`,
      padding: '2px 8px',
      borderRadius: 2,
      transition: 'all 0.4s ease',
    }}>
      {label}
    </span>
  );
}

function ArchCard({ url, title, description, type, status, link }) {
  const [hovered, setHovered] = useState(false);
  const color = COLORS[type] || COLORS.infra;

  const inner = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? `${color}10` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${hovered ? color + '44' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 4,
        padding: '16px 20px',
        transition: 'all 0.25s ease',
        cursor: link ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {/* Top row: URL + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
          fontSize: 11,
          color: color,
          opacity: 0.85,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {url}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <StatusDot status={status} />
          <StatusPill status={status} />
        </div>
      </div>

      {/* Title */}
      <p style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 14,
        color: '#f0f0f5',
        lineHeight: 1.3,
        margin: 0,
      }}>
        {title}
      </p>

      {/* Description */}
      <p style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 12,
        color: 'rgba(240,240,245,0.4)',
        fontWeight: 300,
        lineHeight: 1.45,
        margin: 0,
      }}>
        {description}
      </p>
    </div>
  );

  if (link) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        {inner}
      </a>
    );
  }
  return inner;
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 44 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <p style={{
          fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
          fontSize: 9,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'rgba(240,240,245,0.3)',
          margin: 0,
          whiteSpace: 'nowrap',
        }}>
          {title}
        </p>
        <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.06)' }} />
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
        gap: 10,
      }}>
        {children}
      </div>
    </div>
  );
}

function AIContextCard({ slug, title, description, volatility, updatedAt }) {
  const [hovered, setHovered] = useState(false);
  const color = '#6B35B8';
  const volLabel = volatility || '—';

  return (
    <Link to={`/admin/ai-context/${slug}`} style={{ textDecoration: 'none' }}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: hovered ? `${color}10` : 'rgba(255,255,255,0.03)',
          border: `1px solid ${hovered ? color + '44' : 'rgba(255,255,255,0.06)'}`,
          borderRadius: 4,
          padding: '16px 20px',
          transition: 'all 0.25s ease',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          height: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 11,
              color,
              opacity: 0.85,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            /admin/ai-context/{slug}
          </span>
          <span
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 9,
              letterSpacing: '0.1em',
              color,
              background: `${color}18`,
              border: `0.5px solid ${color}55`,
              padding: '2px 8px',
              borderRadius: 2,
              textTransform: 'uppercase',
            }}
          >
            {volLabel}
          </span>
        </div>
        <p
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 14,
            color: '#f0f0f5',
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          {title}
        </p>
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 12,
            color: 'rgba(240,240,245,0.4)',
            fontWeight: 300,
            lineHeight: 1.45,
            margin: 0,
          }}
        >
          {description}
        </p>
        {updatedAt && (
          <p
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 9,
              letterSpacing: '0.08em',
              color: 'rgba(240,240,245,0.25)',
              margin: '4px 0 0',
            }}
          >
            updated · {new Date(updatedAt).toLocaleDateString()}
          </p>
        )}
      </div>
    </Link>
  );
}

function AIContextSection() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recompiling, setRecompiling] = useState(false);
  const [cleaningLegacy, setCleaningLegacy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = async () => {
    try {
      const res = await api.get(`/admin/ai-context?_=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } });
      setDocs(res.data.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRecompileAll = async () => {
    if (!window.confirm('Recompile every AI Context source document?')) return;
    setRecompiling(true);
    setNotice('');
    try {
      const res = await api.post('/admin/ai-context/recompile-all');
      setNotice(`Recompiled ${res.data.compiled?.length || 0} documents`);
      setTimeout(() => setNotice(''), 6000);
    } catch (err) {
      setNotice(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRecompiling(false);
    }
  };

  const LEGACY_SLUGS = ['werner_ai_context', 'werner_selfportrait', 'werner_claude_preferences'];
  const legacyPresent = docs.some((d) => LEGACY_SLUGS.includes(d.slug));

  const handleCleanupLegacy = async () => {
    if (!window.confirm('Delete the three legacy cards (werner_ai_context, werner_selfportrait, werner_claude_preferences) and their compiled artifacts? This cannot be undone.')) return;
    setCleaningLegacy(true);
    setNotice('');
    try {
      const res = await api.post('/admin/ai-context/legacy-cleanup');
      const removedDocs = res.data.removed_docs || [];
      const removedArts = res.data.removed_artifacts || [];
      setNotice(`Removed ${removedDocs.length} legacy doc${removedDocs.length === 1 ? '' : 's'} and ${removedArts.length} compiled artifact${removedArts.length === 1 ? '' : 's'}`);
      setTimeout(() => setNotice(''), 8000);
      await load();
    } catch (err) {
      setNotice(`Cleanup failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setCleaningLegacy(false);
    }
  };

  const ORDER = [
    'awaken_manual',
    'awaken_blueprint',
    'awaken_signature',
    'zephyr_system_prompt',
    'compiler_instructions',
    'sync_context',
  ];
  const ordered = [...docs].sort((a, b) => ORDER.indexOf(a.slug) - ORDER.indexOf(b.slug));

  return (
    <div style={{ marginBottom: 44 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <p
          style={{
            fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
            fontSize: 9,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(240,240,245,0.3)',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          AI Context
        </p>
        <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.06)' }} />
        {legacyPresent && (
          <button
            onClick={handleCleanupLegacy}
            disabled={cleaningLegacy}
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 9,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: cleaningLegacy ? 'rgba(240,240,245,0.25)' : '#E66B6B',
              background: 'transparent',
              border: `1px solid ${cleaningLegacy ? 'rgba(255,255,255,0.06)' : 'rgba(230,107,107,0.35)'}`,
              padding: '4px 12px',
              borderRadius: 3,
              cursor: cleaningLegacy ? 'default' : 'pointer',
            }}
          >
            {cleaningLegacy ? 'Deleting...' : 'Delete Legacy Cards'}
          </button>
        )}
        <button
          onClick={handleRecompileAll}
          disabled={recompiling}
          style={{
            fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
            fontSize: 9,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: recompiling ? 'rgba(240,240,245,0.25)' : '#f0f0f5',
            background: 'transparent',
            border: `1px solid ${recompiling ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.10)'}`,
            padding: '4px 12px',
            borderRadius: 3,
            cursor: recompiling ? 'default' : 'pointer',
          }}
        >
          {recompiling ? 'Compiling...' : 'Recompile All'}
        </button>
      </div>
      {notice && (
        <div
          style={{
            fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
            fontSize: 10,
            color: '#6B35B8',
            background: '#6B35B812',
            border: '1px solid #6B35B833',
            padding: '8px 12px',
            borderRadius: 3,
            marginBottom: 14,
          }}
        >
          {notice}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
          gap: 10,
        }}
      >
        {loading ? (
          <p
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 11,
              color: 'rgba(240,240,245,0.4)',
            }}
          >
            Loading...
          </p>
        ) : (
          ordered.map((d) => (
            <AIContextCard
              key={d.slug}
              slug={d.slug}
              title={d.title}
              description={d.description}
              volatility={d.volatility}
              updatedAt={d.updated_at}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminArchitecture() {
  const [health, setHealth]               = useState({});
  const [frontendStatus, setFrontendStatus] = useState('unknown');
  const [lastPoll, setLastPoll]           = useState(null);
  const [running, setRunning]             = useState(false);

  const fetchHealth = async () => {
    setRunning(true);

    // Vercel / frontend — hit /api/health
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
      setFrontendStatus(res.ok ? 'ok' : 'error');
    } catch {
      setFrontendStatus('error');
    }

    // Backend services
    try {
      const res = await api.get('/admin/health');
      setHealth(res.data);
    } catch {
      setHealth({
        railway: 'error', mongodb: 'error', cloudinary: 'error',
        anthropic: 'error', openai: 'error', brave: 'error',
      });
    }

    setLastPoll(new Date());
    setRunning(false);
  };

  const s = (key) => health[key] || 'unknown';
  const routeStatus = frontendStatus;

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', padding: '48px 40px 80px' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 52 }}>
          <p style={{
            fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
            fontSize: 9,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(240,240,245,0.25)',
            marginBottom: 12,
          }}>
            Admin / Architecture
          </p>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 30,
            fontWeight: 400,
            color: '#f0f0f5',
            margin: '0 0 16px',
          }}>
            Zephyr OS
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button
              onClick={fetchHealth}
              disabled={running}
              style={{
                fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: running ? 'rgba(240,240,245,0.25)' : '#f0f0f5',
                background: 'transparent',
                border: `1px solid ${running ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.10)'}`,
                padding: '6px 16px',
                borderRadius: 3,
                cursor: running ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseEnter={e => { if (!running) e.currentTarget.style.borderColor = '#6b7084'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = running ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.10)'; }}
            >
              {running && (
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  border: '1.5px solid rgba(240,240,245,0.3)',
                  borderTopColor: 'rgba(240,240,245,0.7)',
                  display: 'inline-block',
                  animation: 'spin 0.7s linear infinite',
                }} />
              )}
              {running ? 'Testing…' : 'Test architecture'}
            </button>
            {lastPoll && (
              <span style={{
                fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                fontSize: 9,
                color: 'rgba(240,240,245,0.2)',
                letterSpacing: '0.08em',
              }}>
                last run {lastPoll.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── AI Context ───────────────────────────────────────────────────── */}
        <AIContextSection />

        {/* ── Public Surfaces ─────────────────────────────────────────────── */}
        <Section title="Public Surfaces">
          {[
            { url: 'wernerdutoit.co.za/gallery',          title: 'Gallery',             description: 'Public hub — Framed, Cult collections, featured work' },
            { url: 'wernerdutoit.co.za/gallery/framed',   title: 'Framed Gallery',      description: 'Public photo collection gallery' },
            { url: 'wernerdutoit.co.za/gallery/cult',     title: 'Cult Gallery',        description: 'Public Cult of One gallery with curated collections' },
            { url: 'wernerdutoit.co.za/gallery/curated/:id', title: 'Curated Collection', description: 'Individual curated public collection view' },
            { url: 'wernerdutoit.co.za/gallery/cult/curated/capetown', title: 'Cape Town', description: 'Cape Town curated public collection' },
            { url: 'wernerdutoit.co.za/travels',          title: 'Travels Gallery',     description: 'Public travel photography and memoir gallery' },
            { url: 'wernerdutoit.co.za/travels/countries',title: 'Countries',           description: 'World map of visited countries' },
            { url: 'wernerdutoit.co.za/trip/:slug',       title: 'Trip Live',           description: 'Live public trip view with real-time updates' },
            { url: 'wernerdutoit.co.za/trip/:slug/memoir',title: 'Trip Memoir',         description: 'Published trip memoir — AI-generated narrative' },
            { url: 'wernerdutoit.co.za/giraffe/memoir/:slug', title: 'Giraffe Memoir',  description: 'Published memoir from Giraffe trip' },
          ].map(card => (
            <ArchCard key={card.url} {...card} type="public" status={routeStatus} />
          ))}
        </Section>

        {/* ── Private Apps ────────────────────────────────────────────────── */}
        <Section title="Private Apps">
          {[
            { url: '/dashboard', title: 'Dashboard',      description: 'Central command — daily overview of all apps' },
            { url: '/uncode',    title: 'Uncode',         description: 'Task management — Chaos into Clarity' },
            { url: '/giraffe',   title: 'Giraffe',        description: 'Travel journaling — The Journey Unraveled' },
            { url: '/framed',    title: 'Framed',         description: 'Photo curation and collections — A Life in Technicolor' },
            { url: '/cult',      title: 'Cult of One',    description: 'Personal philosophy and mantras — Manifest of a Movement' },
            { url: '/dash',      title: 'Dash',           description: 'House and Tannie status, read-only' },
            { url: '/soulsync',  title: 'SoulSync',       description: 'Journal and self-reflection — Architecting The Self' },
            { url: '/zephyr',    title: 'Zephyr',         description: 'Registry: where every brief, spec and decision lives' },
            { url: '/curated/capetown', title: 'Curated Editor', description: 'Admin editor for Cape Town curated collection' },
          ].map(card => (
            <ArchCard key={card.url} {...card} type="private" status={routeStatus} />
          ))}
        </Section>

        {/* ── Infrastructure ───────────────────────────────────────────────── */}
        <Section title="Infrastructure">
          {[
            {
              url:         'vercel.com',
              title:       'Vercel',
              description: 'Frontend host — React 18, Tailwind, CRACO',
              status:      frontendStatus,
              link:        'https://vercel.com/dashboard',
            },
            {
              url:         'railway.app',
              title:       'Railway',
              description: 'Backend host — FastAPI Python',
              status:      s('railway'),
              link:        'https://railway.app',
            },
            {
              url:         'mongodb.com/atlas',
              title:       'MongoDB Atlas',
              description: 'Database — all collections',
              status:      s('mongodb'),
              link:        'https://cloud.mongodb.com',
            },
            {
              url:         'cloudinary.com',
              title:       'Cloudinary',
              description: 'Media storage — images, audio, uploads',
              status:      s('cloudinary'),
              link:        'https://cloudinary.com/console',
            },
            {
              url:         'anthropic.com',
              title:       'Anthropic API',
              description: 'Claude Sonnet 4.6 — Zephyr, Uncode, Giraffe, SoulSync',
              status:      s('anthropic'),
              link:        'https://console.anthropic.com',
            },
            {
              url:         'openai.com/whisper',
              title:       'OpenAI Whisper',
              description: 'Voice transcription — Uncode voice, Universal Input',
              status:      s('openai'),
              link:        'https://platform.openai.com',
            },
            {
              url:         'brave.com/search/api',
              title:       'Brave Search API',
              description: 'Web search — Zephyr research queries',
              status:      s('brave'),
              link:        'https://api.search.brave.com',
            },
          ].map(card => (
            <ArchCard key={card.url} {...card} type="infra" />
          ))}
        </Section>

      </div>
    </div>
  );
}
